import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import { BottomSheet } from '../../weekly/v2/primitives/BottomSheet';
import { useTranslation } from 'react-i18next';

interface Props {
  visible: boolean;
  portions: number;
  fish: number;
  veg: number;
  meat: number;
  onPortionsChange: (n: number) => void;
  onFishChange: (n: number) => void;
  onVegChange: (n: number) => void;
  onMeatChange: (n: number) => void;
  onClose: () => void;
}

function Stepper({ value, onChange, min = 0, max = 14 }: { value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <View style={s.stepper}>
      <Pressable style={[s.stepBtn, value <= min && s.stepBtnDisabled]} onPress={() => onChange(Math.max(min, value - 1))}>
        <Text style={s.stepBtnText}>−</Text>
      </Pressable>
      <Text style={s.stepValue}>{value}</Text>
      <Pressable style={[s.stepBtn, value >= max && s.stepBtnDisabled]} onPress={() => onChange(Math.min(max, value + 1))}>
        <Text style={s.stepBtnText}>+</Text>
      </Pressable>
    </View>
  );
}

function DietRow({ role, label, value, onChange }: { role: string; label: string; value: number; onChange: (n: number) => void }) {
  return (
    <View style={s.row}>
      <View style={s.rowLeft}>
        <DietDot role={role} size={10} />
        <Text style={s.rowLabel}>{label}</Text>
      </View>
      <Stepper value={value} onChange={onChange} />
    </View>
  );
}

export function WeekSheet({ visible, portions, fish, veg, meat, onPortionsChange, onFishChange, onVegChange, onMeatChange, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="80%">
      <View style={s.content}>
        <View style={s.headerBlock}>
          <Text style={s.heading}>{t('mealConfig.weekSettings')}</Text>
          <Text style={s.subheading}>{t('mealConfig.appliesByDefault')}</Text>
        </View>

        {/* Default portions */}
        <View style={s.row}>
          <View style={s.rowLeft}>
            <Feather name="users" size={16} color={Paprika.textSecondary} />
            <Text style={s.rowLabelStrong}>{t('mealConfig.defaultPortions')}</Text>
          </View>
          <Stepper value={portions} onChange={onPortionsChange} min={1} max={12} />
        </View>

        {/* Dietary distribution */}
        <View style={s.sectionHead}>
          <Text style={s.sectionTitle}>{t('mealConfig.targetMix')}</Text>
          <Text style={s.guideNote}>{t('mealConfig.softGuide')}</Text>
        </View>
        <DietRow role="fish" label={t('mealConfig.fish')} value={fish} onChange={onFishChange} />
        <DietRow role="vegetarian" label={t('mealConfig.veggie')} value={veg} onChange={onVegChange} />
        <DietRow role="meat" label={t('mealConfig.meat')} value={meat} onChange={onMeatChange} />

        <Pressable style={s.doneBtn} onPress={onClose}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={s.doneBtnText}>{t('common.done')}</Text>
        </Pressable>
      </View>
    </BottomSheet>
  );
}

const s = StyleSheet.create({
  content: {
    paddingHorizontal: 20,
    paddingTop: 4,
    gap: 18,
  },
  headerBlock: {
    gap: 3,
  },
  heading: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    color: Paprika.textPrimary,
  },
  subheading: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  rowLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 15,
    color: Paprika.textSecondary,
  },
  rowLabelStrong: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.textPrimary,
  },
  sectionHead: {
    gap: 2,
    marginTop: 2,
  },
  sectionTitle: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.textPrimary,
  },
  guideNote: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: 14,
    overflow: 'hidden',
  },
  stepBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepBtnDisabled: {
    opacity: 0.35,
  },
  stepBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 20,
    color: Paprika.textSecondary,
    lineHeight: 22,
  },
  stepValue: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 18,
    color: Paprika.textPrimary,
    minWidth: 22,
    textAlign: 'center',
  },
  doneBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 15,
    marginTop: 4,
    marginBottom: 8,
  },
  doneBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: '#fff',
  },
});
